import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, MaskLines } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { IMAGES, SERVICES, WHY_OUTDOOR } from "@/lib/data";

export default function Services() {
  return (
    <div data-testid="services-page">
      <Seo title="Our Services — Railway, Digital, Outdoor & Transit Advertising" description="Explore Twins Media's advertising services: railway station advertising, digital display screens, outdoor hoardings, transit advertising and brand promotion campaigns across India." path="/services" />
      <section className="relative pt-40 md:pt-52 pb-20 md:pb-28 overflow-hidden bg-maroon text-cream">
        <div className="absolute inset-0 -z-10 opacity-25">
          <img src={IMAGES.digitalScreens} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-maroon/70" />
        </div>
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-gold">
            <span className="h-px w-8 bg-gold" />Our Services
          </motion.span>
          <h1 className="mt-6 font-display text-6xl md:text-8xl leading-[0.95] tracking-tight">
            <MaskLines lines={["Advertising", <span key="s" className="italic text-gold">That Moves</span>]} start={0.1} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }} className="mt-8 text-cream/80 text-lg max-w-2xl leading-relaxed">
            End-to-end out-of-home advertising — from railway platforms to digital screens, hoardings and multi-city brand campaigns.
          </motion.p>
        </div>
      </section>

      <section className="bg-cream">
        {SERVICES.map((s, i) => {
          const flip = i % 2 === 1;
          return (
            <div key={s.id} className="border-b border-charcoal/10" data-testid={`service-${s.id}`}>
              <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                <div className={`lg:col-span-7 ${flip ? "lg:order-2" : ""}`}>
                  <Reveal>
                    <div className="group relative overflow-hidden">
                      <img src={s.image} alt={s.title} className="w-full h-[300px] md:h-[440px] object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 ring-1 ring-inset ring-charcoal/10" />
                    </div>
                  </Reveal>
                </div>
                <div className={`lg:col-span-5 ${flip ? "lg:order-1" : ""}`}>
                  <Reveal delay={0.05}>
                    <span className="font-display text-6xl text-gold/70">{s.no}</span>
                    <h2 className="mt-4 font-display text-4xl md:text-5xl tracking-tight text-charcoal leading-tight">{s.title}</h2>
                    <p className="mt-6 text-charcoal-muted text-lg leading-relaxed">{s.desc}</p>
                    <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-maroon hover:gap-4 transition-all duration-300">
                      Enquire Now <ArrowUpRight size={16} />
                    </Link>
                  </Reveal>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <section className="py-24 md:py-32 bg-maroon text-cream" data-testid="benefits-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="mb-14 max-w-2xl">
            <Reveal><span className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-gold"><span className="h-px w-8 bg-gold" />The Benefits</span></Reveal>
            <Reveal delay={0.05}><h2 className="mt-6 font-display text-5xl md:text-6xl tracking-tight">Why It Works</h2></Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-cream/15">
            {WHY_OUTDOOR.map((b, i) => (
              <Reveal key={b} delay={(i % 3) * 0.05}>
                <div className="bg-maroon p-8 h-full">
                  <span className="font-display text-4xl text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-4 font-display text-2xl">{b}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <h2 className="font-display text-5xl md:text-7xl tracking-tight text-charcoal">Ready to get <span className="italic text-maroon">seen?</span></h2>
            <Link to="/contact" data-testid="services-cta-btn" className="mt-10 inline-flex items-center gap-3 bg-maroon text-cream px-10 py-4 text-sm uppercase tracking-[0.18em] hover:bg-maroon-hover transition-colors duration-300">
              Request a Quote <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
